/**
 * Ellis Brooklyn custom modules.
 * Feature modules use window.Utility helpers from utility.js.
 */
(function () {
  'use strict';

  const U = window.Utility;
  if (!U) {
    console.warn('[custom.js] utility.js must load before custom.js');
    return;
  }

  const ADD_BUTTON_SELECTOR =
    '.rebuy-button:not(.outline):not(.decline):not(.product-quick-view-button):not(.rebuy-bundle-view-btn)';

  const TITLE_SELECTORS = [
    '.rebuy-product-info .rebuy-product-title-link',
    '.rebuy-product-info .rebuy-product-title-trigger',
    '.rebuy-product-info .rebuy-product-title',
    '.rebuy-product-title-link',
    '.rebuy-product-title-trigger',
    '.rebuy-product-title',
    '.rebuy-variant-title',
    '.primary-title',
    '.super-title',
  ];

  /**
   * Rebuy widget 295743 — PDP set enhancements + Smart Cart on add.
   */
  const RebuySetsWidget = {
    name: 'RebuySetsWidget',
    WIDGET_ID: '295743',
    _mutationObserver: null,

    get selector() {
      return `[data-rebuy-id="${this.WIDGET_ID}"]`;
    },

    getRoot() {
      return U.DomSelector.$(this.selector);
    },

    getBlockProductId(block) {
      const match = block.className.match(/product-id-(\d+)/);
      return match ? match[1] : null;
    },

    getWidgetProductForBlock(root, block) {
      const widget = U.Rebuy.getWidget(root);
      const products = widget?.data?.products;
      if (!products?.length) return null;

      const productId = this.getBlockProductId(block);
      if (productId) {
        for (let i = 0; i < products.length; i += 1) {
          if (String(products[i].id) === productId) return products[i];
        }
      }

      const blocks = root.querySelectorAll('.rebuy-product-block');
      const index = Array.prototype.indexOf.call(blocks, block);
      return index >= 0 ? products[index] || null : null;
    },

    getBlockProductUrl(block) {
      const link = block.querySelector('a[href*="/products/"]');
      return link ? link.href : '';
    },

    findTitleSource(block) {
      for (let i = 0; i < TITLE_SELECTORS.length; i += 1) {
        const element = block.querySelector(TITLE_SELECTORS[i]);
        const text = U.DOM.readRebuyTitleText(element);
        if (text) return { element, text };
      }
      return null;
    },

    getTitleFallback(block, root) {
      const image = block.querySelector('.rebuy-product-media img[alt], img[alt]');
      if (image?.alt) {
        const alt = image.alt
          .replace(/^View\s+/i, '')
          .replace(/\s+/g, ' ')
          .trim();
        if (alt && !/^product(\s+image)?$/i.test(alt)) return alt;
      }

      const labelledLink = block.querySelector('[aria-label*="View "], [aria-label*="Quick view "]');
      if (labelledLink) {
        const label = U.DOM.readRebuyTitleText(labelledLink);
        if (label) return label;
      }

      const widgetProduct = this.getWidgetProductForBlock(root, block);
      return widgetProduct?.title ? String(widgetProduct.title).trim() : '';
    },

    ensureProductTitle(block, root) {
      const info = block.querySelector('.rebuy-product-info');
      if (!info) return false;

      let source = this.findTitleSource(block);
      let titleText = source ? source.text : '';
      let titleElement = source ? source.element : null;

      if (!titleText) titleText = this.getTitleFallback(block, root);
      if (!titleText) return false;

      if (titleElement && !info.contains(titleElement)) {
        const titleContainer = titleElement.closest('.rebuy-product-title') || titleElement;
        info.insertBefore(titleContainer, info.firstChild);
        titleElement =
          titleContainer.querySelector('.rebuy-product-title-link, .rebuy-product-title-trigger') ||
          titleContainer;
      }

      let titleLink = info.querySelector('.rebuy-product-title-link');
      const titleTrigger = info.querySelector('.rebuy-product-title-trigger');

      if (titleLink) {
        if (!U.DOM.readRebuyTitleText(titleLink)) titleLink.textContent = titleText;
        if (!titleLink.getAttribute('href')) {
          const href = this.getBlockProductUrl(block);
          if (href) titleLink.href = href;
        }
        return true;
      }

      if (titleTrigger) {
        if (!U.DOM.readRebuyTitleText(titleTrigger)) titleTrigger.textContent = titleText;
        return true;
      }

      const titleWrapper = document.createElement('h5');
      titleWrapper.className = 'rebuy-product-title';

      titleLink = document.createElement('a');
      titleLink.className = 'rebuy-product-title-link';
      titleLink.textContent = titleText;
      titleLink.setAttribute('aria-label', `View ${titleText}`);
      titleLink.rel = 'nofollow';

      const href = this.getBlockProductUrl(block);
      if (href) titleLink.href = href;

      titleWrapper.appendChild(titleLink);
      info.insertBefore(titleWrapper, info.firstChild);
      return true;
    },

    moveWidgetCopyIntoProductInfo(root) {
      const block = root.querySelector('.rebuy-product-block');
      const info = block?.querySelector('.rebuy-product-info');
      const content = root.querySelector('.rebuy-widget-content');
      if (!info || !content) return;

      [
        { selector: '.primary-title', position: 'start' },
        { selector: '.description', position: 'end' },
      ].forEach((item) => {
        const element = content.querySelector(item.selector);
        if (element && !info.contains(element)) {
          if (item.position === 'start') {
            info.insertBefore(element, info.firstChild);
          } else {
            info.appendChild(element);
          }
        }
      });
    },

    getStrings() {
      return U.Rebuy.getWidgetStrings(this.WIDGET_ID);
    },

    applyButtonTranslations(root) {
      if (!root) return;

      const strings = this.getStrings();
      root.querySelectorAll('.rebuy-product-block').forEach((block) => {
        this.normalizeAddToCartButton(block, strings);

        const viewButton = block.querySelector('.rebuy-bundle-view-btn');
        if (viewButton) {
          if (strings.view_set) viewButton.textContent = strings.view_set;
          viewButton.setAttribute(
            'aria-label',
            strings.view_set_aria_label || strings.view_set || 'View set'
          );
        }
      });
    },

    ensureViewSetButton(block, strings = this.getStrings()) {
      const actions = block.querySelector('.rebuy-product-actions');
      if (!actions || actions.querySelector('.rebuy-bundle-view-btn')) return;

      const titleLink =
        block.querySelector('.rebuy-product-title-link') ||
        block.querySelector('.rebuy-product-title-trigger');
      const href = titleLink
        ? titleLink.href || titleLink.getAttribute('data-url') || this.getBlockProductUrl(block)
        : this.getBlockProductUrl(block);

      if (!href) return;

      const viewButton = document.createElement('a');
      viewButton.className = 'rebuy-button outline rebuy-bundle-view-btn';
      viewButton.href = href;
      viewButton.textContent = strings.view_set || 'View set';
      viewButton.setAttribute(
        'aria-label',
        strings.view_set_aria_label || strings.view_set || 'View set'
      );
      actions.insertBefore(viewButton, actions.firstChild);
    },

    normalizeAddToCartButton(block, strings = this.getStrings()) {
      const actions = block.querySelector('.rebuy-product-actions');
      if (!actions) return;

      const addButton = actions.querySelector(ADD_BUTTON_SELECTOR);
      if (!addButton) return;

      const label = addButton.querySelector('span') || addButton;
      label.textContent = strings.add_to_cart || 'Add to cart';
    },

    wireAddToCartHandler(root, block) {
      const addButton = block.querySelector(ADD_BUTTON_SELECTOR);
      if (!addButton || addButton.dataset.ellisAddWired === 'true') return;

      addButton.dataset.ellisAddWired = 'true';
      addButton.addEventListener(
        'click',
        async (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (addButton.dataset.adding === 'true' || addButton.disabled) return;

          const variantId = U.Rebuy.getSelectedVariantId(block, root);
          if (!variantId) {
            console.warn('[RebuySetsWidget] Missing variant id for add to cart');
            return;
          }

          const strings = this.getStrings();
          const label = addButton.querySelector('span') || addButton;
          const defaultLabel = strings.add_to_cart || label.textContent.trim() || 'Add to cart';

          addButton.dataset.adding = 'true';
          addButton.disabled = true;
          label.textContent = strings.adding_to_cart || 'Adding to cart...';
          root.dataset.pendingDrawerOpen = 'true';

          try {
            await U.Rebuy.addViaShopify(
              {
                id: variantId,
                quantity: 1,
                properties: {
                  _source: 'Rebuy',
                  _widget_id: this.WIDGET_ID,
                },
              },
              { showSmartCart: true }
            );

            document.dispatchEvent(
              new CustomEvent('rebuy.add', {
                detail: { widget: U.Rebuy.getWidget(root) },
              })
            );
          } catch (error) {
            root.dataset.pendingDrawerOpen = '';
            console.error('[RebuySetsWidget] Add to cart failed:', error);
            U.handleError(error, 'Add to cart');
          } finally {
            addButton.dataset.adding = 'false';
            addButton.disabled = false;
            label.textContent = defaultLabel;
          }
        },
        true
      );
    },

    hideSingleItemCarouselChrome(root) {
      const slides = root.querySelectorAll('.splide__slide, .rebuy-product-block');
      if (slides.length > 1) return;

      U.DomSelector.$$('.splide__arrows, .splide__pagination, .rebuy-carousel__arrow', root).forEach(
        (node) => {
          node.style.display = 'none';
        }
      );
    },

    enhanceWidget(root) {
      if (!root || root.dataset.enhanced === 'true') return false;

      const blocks = root.querySelectorAll('.rebuy-product-block');
      if (!blocks.length) return false;

      this.moveWidgetCopyIntoProductInfo(root);

      let allHaveTitles = true;

      blocks.forEach((block) => {
        if (!this.ensureProductTitle(block, root)) allHaveTitles = false;
        this.ensureViewSetButton(block);
        this.normalizeAddToCartButton(block);
        this.wireAddToCartHandler(root, block);
      });

      this.applyButtonTranslations(root);

      this.hideSingleItemCarouselChrome(root);

      if (allHaveTitles) {
        root.dataset.enhanced = 'true';
        root.dataset.titleRetries = '0';
        U.CSS.add(root, 'is-enhanced');
        return true;
      }

      const retries = Number(root.dataset.titleRetries || 0);
      if (retries >= 12) {
        root.dataset.enhanced = 'true';
        U.CSS.add(root, 'is-enhanced');
        return true;
      }

      root.dataset.titleRetries = String(retries + 1);

      if (root.dataset.titleRetryScheduled === 'true') return false;

      root.dataset.titleRetryScheduled = 'true';

      setTimeout(() => {
        root.dataset.titleRetryScheduled = '';

        blocks.forEach((block) => {
          this.ensureProductTitle(block, root);
          this.ensureViewSetButton(block);
        });

        this.applyButtonTranslations(root);

        const titlesReady = Array.prototype.every.call(blocks, (block) =>
          this.ensureProductTitle(block, root)
        );

        if (titlesReady || Number(root.dataset.titleRetries || 0) >= 12) {
          root.dataset.enhanced = 'true';
          root.dataset.titleRetries = '0';
          U.CSS.add(root, 'is-enhanced');
          return;
        }

        this.enhanceWidget(root);
      }, 150);

      return false;
    },

    tryEnhance() {
      const root = this.getRoot();
      if (!root || root.dataset.enhanced === 'true') return;
      this.enhanceWidget(root);
    },

    resetEnhancement(root) {
      if (!root) return;
      root.dataset.enhanced = 'false';
      root.dataset.titleRetries = '0';
      root.dataset.titleRetryScheduled = '';
      U.CSS.remove(root, 'is-enhanced');
    },

    bindEvents() {
      document.addEventListener('rebuy.add', (event) => {
        const widget = event.detail?.widget;
        const root = this.getRoot();
        const fromWidget =
          (widget && String(widget.id) === this.WIDGET_ID) ||
          (root && root.dataset.pendingDrawerOpen === 'true');

        if (!fromWidget) return;

        if (root) root.dataset.pendingDrawerOpen = '';
        U.Rebuy.scheduleShowSmartCartWithRetry();
        U.Alpine.syncCartFromRebuy({ skipRebuyFetch: true, lightweight: true });
      });

      U.Rebuy.on('rebuy.ready', this.WIDGET_ID, (event) => {
        const root = this.getRoot();
        U.Rebuy.setWidgetRef(root, event.detail?.widget);
        this.tryEnhance();
        if (root) this.applyButtonTranslations(root);
      });

      U.Rebuy.on('rebuy.productsChange', this.WIDGET_ID, (event) => {
        const root = this.getRoot();
        U.Rebuy.setWidgetRef(root, event.detail?.widget);
        this.resetEnhancement(root);
        this.tryEnhance();
      });
    },

    observe() {
      const onMutation = U.debounce(() => {
        const root = this.getRoot();
        if (root && root.dataset.enhanced !== 'true' && root.querySelector('.rebuy-product-block')) {
          this.tryEnhance();
        }
      }, 100);

      if (this._mutationObserver) return;

      this._mutationObserver = new MutationObserver(onMutation);
      this._mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
    },

    init() {
      U.Rebuy.exposeGlobals();
      this.bindEvents();
      this.observe();
      this.tryEnhance();
    },
  };

  /**
   * Free samples — dropdown + progress bar in Rebuy Smart Cart (and theme cart source).
   */
  const FreeSamples = {
    name: 'FreeSamples',
    REBUY_MOUNT: U.Samples.REBUY_MOUNT,

    decodeDataText(value) {
      return U.decodeHtmlEntities(value);
    },

    isSampleOptionSoldOut(option) {
      if (!option) return true;
      return (
        option.disabled ||
        option.getAttribute('aria-disabled') === 'true' ||
        option.dataset.available === 'false' ||
        option.classList.contains('is-sold-out')
      );
    },

    applySampleOptionSoldOutLabels(root) {
      if (!root) return;

      const soldOutLabel = this.decodeDataText(
        root.dataset.soldOutLabel || root.dataset.soldOutLabelFallback || 'Sold out'
      );

      root.querySelectorAll('.sample-select-option.is-sold-out').forEach((option) => {
        option.disabled = true;
        option.setAttribute('aria-disabled', 'true');

        const badge = option.querySelector('.sample-option-sold-out');
        if (badge) {
          badge.textContent = soldOutLabel;
          badge.hidden = false;
          badge.setAttribute('aria-hidden', 'false');
          U.CSS.remove(badge, 'hidden');
        }
      });
    },

    getPickerStatusMessage(root, totalSamples, maxFree) {
      const pickSamples = this.decodeDataText(root.dataset.pickSamples);
      const pickOneMore = this.decodeDataText(root.dataset.pickOneMore);
      const allSelected = this.decodeDataText(root.dataset.allSelected);

      if (totalSamples >= maxFree) return allSelected;
      if (maxFree > 1 && totalSamples === maxFree - 1) return pickOneMore;
      return pickSamples;
    },

    resetSampleBadges(options) {
      options.forEach((option) => {
        const badge = option.querySelector('.sample-option-added');
        if (!badge) return;
        badge.textContent = '';
        badge.hidden = true;
        badge.setAttribute('aria-hidden', 'true');
        U.CSS.remove(badge, 'is-visible');
        U.CSS.add(badge, 'hidden');
      });
    },

    wireSampleDropdown() {
      /* Delegation removed — per-selector listeners in initSampleSelector (oldcustom pattern). */
    },

    prepareRebuySampleSelector() {
      const rebuyCart = U.Rebuy.findCartRoot();
      if (!rebuyCart) return false;

      let selector = rebuyCart.querySelector(
        `.sample-selector[data-sample-mount="${this.REBUY_MOUNT}"]`
      );
      const progress = rebuyCart.querySelector(
        `.sample-progress[data-sample-mount="${this.REBUY_MOUNT}"]`
      );

      if (!selector) {
        this.scheduleRebuyMount({ aggressive: true });
        return false;
      }

      this.ensureSelectorMount(rebuyCart, selector);
      U.Samples.setVisible(selector, true);
      if (progress) {
        U.Samples.setVisible(
          progress,
          U.Samples.isSampleProgressEnabled() && U.Samples.cartHasItems('rebuy')
        );
      }

      U.Samples.applySampleTranslations(selector);
      if (progress) {
        U.Samples.applySampleTranslations(progress);
        U.Samples.updateProgress(progress, 'rebuy', U.Samples.getMaxFreeSamples(progress), {
          qualifies: U.Samples.cartQualifiesForSamples('rebuy'),
          hasItems: U.Samples.cartHasItems('rebuy'),
        });
      }

      if (selector.dataset.initialized !== 'true') {
        this.initSampleSelector(selector, { cartMode: 'rebuy', progressEl: progress });
      } else {
        selector._sampleRefreshUI?.();
      }
      return true;
    },

    initSampleSelector(root, config = {}) {
      if (!root || root.dataset.initialized === 'true') return;

      const S = U.Samples;
      const cartMode = config.cartMode || 'alpine';
      const progress = config.progressEl || U.DomSelector.byId('sample-progress');

      S.applySampleTranslations(root);
      if (progress) S.applySampleTranslations(progress);
      const toggle = root.querySelector('.sample-select-toggle');
      const label = root.querySelector('.sample-select-label');
      const list = root.querySelector('.sample-select-list');
      const image = root.querySelector('.sample-selector-image');
      const status = root.querySelector('.sample-select-status');
      const options = root.querySelectorAll('.sample-select-option');
      const placeholder = this.decodeDataText(root.dataset.selectPlaceholder);
      const addedLabel = this.decodeDataText(root.dataset.addedLabel);
      const addingLabel = this.decodeDataText(root.dataset.addingLabel);

      if (!toggle || !list || !options.length) return;

      this.resetSampleBadges(options);
      this.applySampleOptionSoldOutLabels(root);
      root.dataset.initialized = 'true';

      let refreshQueued = false;
      let adding = false;
      let successTimer = null;

      const setActivityState = (state) => {
        U.CSS.toggle(root, 'is-adding', state === 'adding');
        U.CSS.toggle(root, 'is-success', state === 'success');
      };

      const announce = (message) => {
        if (status) status.textContent = message;
        U.announceToScreenReader(message);
      };

      const closeList = () => {
        list.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        U.CSS.remove(root, 'is-dropdown-open');
        list.style.cssText = '';
      };

      const positionDropdown = () => {
        const viewportGutter = 12;
        const gap = 8;
        const preferredMaxHeight = 320;
        const rect = toggle.getBoundingClientRect();
        const spaceAbove = Math.max(0, rect.top - viewportGutter);
        const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - viewportGutter);
        const openUp = spaceAbove >= spaceBelow;
        const available = (openUp ? spaceAbove : spaceBelow) - gap;
        const maxHeight = Math.max(80, Math.min(preferredMaxHeight, available));

        let left = rect.left;
        const width = rect.width;
        const maxRight = window.innerWidth - viewportGutter;
        if (left + width > maxRight) left = Math.max(viewportGutter, maxRight - width);

        list.style.position = 'fixed';
        list.style.left = `${left}px`;
        list.style.width = `${width}px`;
        list.style.maxHeight = `${maxHeight}px`;
        list.style.right = 'auto';

        if (openUp) {
          list.style.bottom = `${window.innerHeight - rect.top + gap}px`;
          list.style.top = 'auto';
        } else {
          list.style.top = `${rect.bottom + gap}px`;
          list.style.bottom = 'auto';
        }
      };

      const openList = () => {
        if (adding) return;
        list.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        U.CSS.add(root, 'is-dropdown-open');
        positionDropdown();
      };

      const refreshUI = () => {
        if (adding) return;

        const cartReady = S.isCartReady(cartMode);
        const maxFree = S.getMaxFreeSamples(root);
        const hasItems = cartReady && S.cartHasItems(cartMode);
        const qualifies = cartReady && S.cartQualifiesForSamples(cartMode);
        const sampleCounts = cartReady ? S.getSampleQuantityByVariant(cartMode) : {};
        const totalSampleQuantity = cartReady ? S.getTotalSampleQuantity(cartMode) : 0;
        const freeSampleQuantity = cartReady
          ? S.getProgressFreeSampleQuantity(cartMode, maxFree)
          : 0;

        S.setVisible(root, true);

        if (progress) {
          S.updateProgress(progress, cartMode, maxFree, {
            qualifies,
            hasItems,
            freeSampleQuantity,
          });
        }

        const pickerStatusEl = root.querySelector('.sample-selector-picker-status');
        const qualifiedTitleEl = root.querySelector('.sample-selector-qualified-title');
        // const samplesNoteEl = root.querySelector('.sample-selector-samples-note');
        const pickSamples = this.decodeDataText(root.dataset.pickSamples);
        const samplesNote = this.decodeDataText(root.dataset.samplesNote || '');
        const qualifiedHeading = this.decodeDataText(
          root.dataset.qualifiedHeading || root.dataset.qualifiedHeadingFallback || ''
        );

        if (qualifiedTitleEl && qualifiedHeading) {
          qualifiedTitleEl.textContent = qualifiedHeading;
        }

        if (!cartReady || !hasItems) {
          if (pickerStatusEl) {
            pickerStatusEl.textContent = pickSamples;
            S.setVisible(pickerStatusEl, !!pickSamples);
          }
          // if (samplesNoteEl) S.setVisible(samplesNoteEl, false);
          this.resetSampleBadges(options);
          return;
        }

        if (!qualifies) {
          if (pickerStatusEl) {
            pickerStatusEl.textContent = pickSamples;
            S.setVisible(pickerStatusEl, !!pickSamples);
          }
          // if (samplesNoteEl) S.setVisible(samplesNoteEl, false);
          this.resetSampleBadges(options);
          return;
        }

        const pickerStatus = this.getPickerStatusMessage(root, totalSampleQuantity, maxFree);

        if (pickerStatusEl) {
          pickerStatusEl.textContent = pickerStatus || pickSamples;
          S.setVisible(pickerStatusEl, !!(pickerStatus || pickSamples));
        }

        // if (samplesNoteEl) {
        //   const showSamplesNote = totalSampleQuantity >= maxFree && samplesNote;
        //   samplesNoteEl.textContent = samplesNote;
        //   S.setVisible(samplesNoteEl, showSamplesNote);
        // }

        if (!root.classList.contains('is-success') && label) {
          label.textContent = placeholder;
        }

        this.resetSampleBadges(options);

        options.forEach((option) => {
          if (this.isSampleOptionSoldOut(option)) return;

          const id = Number(option.dataset.variantId);
          const count = sampleCounts[id] || 0;
          const badge = option.querySelector('.sample-option-added');
          if (!badge || count <= 0) return;

          badge.textContent = count > 1 ? `${addedLabel} (${count})` : addedLabel;
          badge.hidden = false;
          badge.setAttribute('aria-hidden', 'false');
          U.CSS.add(badge, 'is-visible');
          U.CSS.remove(badge, 'hidden');
        });
      };

      const queueProgressRefresh = (retryPass = 0) => {
        if (refreshQueued) return;
        refreshQueued = true;

        requestAnimationFrame(() => {
          refreshQueued = false;
          refreshUI();

          if (retryPass >= 2 || !progress) return;

          setTimeout(() => {
            refreshUI();
            queueProgressRefresh(retryPass + 1);
          }, 400);
        });
      };

      root._sampleQueueRefresh = queueProgressRefresh;
      root._sampleRefreshUI = refreshUI;

      const onDropdownLayout = () => {
        if (!list.hidden) positionDropdown();
      };

      const showSuccess = (title) => {
        clearTimeout(successTimer);
        setActivityState('success');
        if (label) label.textContent = `${addedLabel} – ${title}`;
        announce(`${title} ${addedLabel}`);

        successTimer = setTimeout(() => {
          setActivityState('');
          queueProgressRefresh();
        }, 1500);
      };

      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        if (adding) return;
        if (list.hidden) openList();
        else closeList();
      });

      root.addEventListener('click', (event) => event.stopPropagation());

      document.addEventListener('click', () => {
        if (!adding) closeList();
      });

      window.addEventListener('resize', onDropdownLayout);
      window.addEventListener('scroll', onDropdownLayout, true);

      options.forEach((option) => {
        option.addEventListener('click', async (event) => {
          event.stopPropagation();
          if (adding || this.isSampleOptionSoldOut(option)) return;

          const id = Number(option.dataset.variantId);
          const title = this.decodeDataText(option.dataset.title || '');
          if (!id) return;

          adding = true;
          closeList();
          setActivityState('adding');

          if (option.dataset.image && image) {
            image.style.opacity = '0.5';
            image.src = option.dataset.image;
            image.onload = () => {
              image.style.opacity = '1';
            };
          }

          if (label) label.textContent = `${addingLabel} ${title}`;
          announce(`${addingLabel} ${title}`);

          try {
            await S.addSample(id, cartMode);
            adding = false;
            showSuccess(title);
            queueProgressRefresh();
          } catch (error) {
            console.error('Failed to add sample:', error);
            adding = false;
            setActivityState('');
            if (label) label.textContent = placeholder;
            announce('Failed to add sample');
            U.handleError(error, 'Sample');
            queueProgressRefresh();
          }
        });
      });

      S.setVisible(root, true);
      queueProgressRefresh();

      if (cartMode === 'alpine') {
        const cartItemsList = U.DomSelector.$('#cart-drawer ul');
        if (cartItemsList) {
          new MutationObserver(queueProgressRefresh).observe(cartItemsList, {
            childList: true,
            subtree: true,
          });
        }

        document.addEventListener(
          'alpine:initialized',
          () => {
            queueProgressRefresh();
            const cart = U.Alpine.getCartStore();
            if (!cart || !window.Alpine?.effect) return;
            window.Alpine.effect(() => {
              const items = cart.items ?? cart.data?.items ?? [];
              items.length;
              queueProgressRefresh();
            });
          },
          { once: true }
        );
      }
    },

    findRebuySubtotalElement(rebuyCart) {
      return (
        rebuyCart.querySelector('[data-rebuy-component="cart-subtotal"]') ||
        rebuyCart.querySelector('[data-rebuy-cart-anchor="subtotal"]') ||
        rebuyCart.querySelector('cart-subtotal') ||
        rebuyCart.querySelector('.rebuy-cart__flyout-subtotal')
      );
    },

    isSelectorPlaced(rebuyCart, selectorMount) {
      if (!rebuyCart?.contains(selectorMount)) return false;

      const subtotalEl = this.findRebuySubtotalElement(rebuyCart);
      if (subtotalEl?.parentElement) {
        return (
          selectorMount.parentElement === subtotalEl.parentElement &&
          selectorMount.nextElementSibling === subtotalEl
        );
      }

      const legacyTarget = rebuyCart.querySelector('[data-app-target="above_subtotal"]');
      if (legacyTarget) return legacyTarget.contains(selectorMount);

      const discountCode =
        rebuyCart.querySelector('discount-code') ||
        rebuyCart.querySelector('[data-rebuy-component="discount-code"]');
      if (discountCode?.parentElement) {
        return (
          selectorMount.parentElement === discountCode.parentElement &&
          selectorMount.nextElementSibling === discountCode
        );
      }

      const flyoutFooter = rebuyCart.querySelector('.rebuy-cart__flyout-footer');
      if (flyoutFooter?.parentElement) {
        return (
          selectorMount.parentElement === flyoutFooter.parentElement &&
          selectorMount.nextElementSibling === flyoutFooter
        );
      }

      return rebuyCart.contains(selectorMount);
    },

    ensureSelectorMount(rebuyCart, selectorMount) {
      if (!rebuyCart || !selectorMount) return false;
      if (this.isSelectorPlaced(rebuyCart, selectorMount)) return true;
      this.insertSelectorMount(rebuyCart, selectorMount);
      return this.isSelectorPlaced(rebuyCart, selectorMount);
    },

    insertSelectorMount(rebuyCart, selectorMount) {
      if (this.isSelectorPlaced(rebuyCart, selectorMount)) return;

      const subtotalEl = this.findRebuySubtotalElement(rebuyCart);
      if (subtotalEl?.parentElement) {
        subtotalEl.parentElement.insertBefore(selectorMount, subtotalEl);
        return;
      }

      const legacyTarget = rebuyCart.querySelector('[data-app-target="above_subtotal"]');
      if (legacyTarget) {
        legacyTarget.appendChild(selectorMount);
        return;
      }

      const discountCode =
        rebuyCart.querySelector('discount-code') ||
        rebuyCart.querySelector('[data-rebuy-component="discount-code"]');
      if (discountCode?.parentElement) {
        discountCode.parentElement.insertBefore(selectorMount, discountCode);
        return;
      }

      const flyoutFooter = rebuyCart.querySelector('.rebuy-cart__flyout-footer');
      if (flyoutFooter?.parentElement) {
        flyoutFooter.parentElement.insertBefore(selectorMount, flyoutFooter);
        return;
      }

      rebuyCart.appendChild(selectorMount);
    },

    isProgressPlaced(rebuyCart, progressMount) {
      if (!rebuyCart?.contains(progressMount)) return false;

      const goalBox = rebuyCart.querySelector('[data-rebuy-cart-anchor="goalBox"]');
      if (goalBox) return progressMount.nextElementSibling === goalBox;

      const flyoutBody = rebuyCart.querySelector('.rebuy-cart__flyout-body');
      if (flyoutBody?.contains(progressMount)) return true;

      const titleBar = rebuyCart.querySelector('[data-rebuy-component="title-bar"]');
      if (titleBar && progressMount.previousElementSibling === titleBar) return true;

      const cartItems = rebuyCart.querySelector('[data-rebuy-component="cart-items"]');
      if (cartItems?.parentElement) {
        return (
          progressMount.parentElement === cartItems.parentElement &&
          progressMount.nextElementSibling === cartItems
        );
      }

      return rebuyCart.firstElementChild === progressMount;
    },

    ensureProgressMount(rebuyCart, progressMount) {
      if (!rebuyCart || !progressMount) return false;
      if (this.isProgressPlaced(rebuyCart, progressMount)) return true;
      this.insertProgressMount(rebuyCart, progressMount);
      return this.isProgressPlaced(rebuyCart, progressMount);
    },

    insertProgressMount(rebuyCart, progressMount) {
      if (this.isProgressPlaced(rebuyCart, progressMount)) return;

      const goalBox = rebuyCart.querySelector('[data-rebuy-cart-anchor="goalBox"]');
      if (goalBox) {
        goalBox.insertAdjacentElement('beforebegin', progressMount);
        return;
      }

      const flyoutBody = rebuyCart.querySelector('.rebuy-cart__flyout-body');
      if (flyoutBody) {
        const anchor =
          flyoutBody.querySelector('.rebuy-cart__flyout-shipping-bar') ||
          flyoutBody.querySelector('.rebuy-cart__flyout-announcement-bar');
        if (anchor) anchor.insertAdjacentElement('afterend', progressMount);
        else flyoutBody.prepend(progressMount);
        return;
      }

      const titleBar = rebuyCart.querySelector('[data-rebuy-component="title-bar"]');
      if (titleBar) {
        titleBar.insertAdjacentElement('afterend', progressMount);
        return;
      }

      const cartItems = rebuyCart.querySelector('[data-rebuy-component="cart-items"]');
      if (cartItems?.parentElement) {
        cartItems.parentElement.insertBefore(progressMount, cartItems);
        return;
      }

      rebuyCart.prepend(progressMount);
    },

    isRebuyCartRendered(rebuyCart) {
      if (!rebuyCart) return false;
      return !!(
        this.findRebuySubtotalElement(rebuyCart) ||
        rebuyCart.querySelector('[data-rebuy-component="cart-items"]') ||
        rebuyCart.querySelector('.rebuy-cart__flyout-item') ||
        rebuyCart.querySelector('cart-items')
      );
    },

    positionSelectorAboveSubtotal(rebuyCart, selectorMount, durationMs = 4000) {
      if (this.ensureSelectorMount(rebuyCart, selectorMount)) {
        U.Samples.setVisible(selectorMount, true);
        return;
      }

      const startedAt = Date.now();
      const tick = () => {
        if (this.ensureSelectorMount(rebuyCart, selectorMount)) {
          U.Samples.setVisible(selectorMount, true);
          return;
        }
        if (Date.now() - startedAt < durationMs) requestAnimationFrame(tick);
      };
      tick();
    },

    async mountRebuySmartCart(options = {}) {
      const S = U.Samples;
      if (S._mountInProgress && !options.force) return false;

      const rebuyCart = U.Rebuy.findCartRoot();
      if (!rebuyCart || !this.isRebuyCartRendered(rebuyCart)) return false;

      const existingMount = rebuyCart.querySelector(
        `.sample-selector[data-sample-mount="${this.REBUY_MOUNT}"]`
      );

      const progressMount = rebuyCart.querySelector(
        `.sample-progress[data-sample-mount="${this.REBUY_MOUNT}"]`
      );

      if (existingMount && !options.force) {
        U.Samples.applySampleTranslations(existingMount);
        this.ensureSelectorMount(rebuyCart, existingMount);
        U.Samples.setVisible(existingMount, true);

        if (existingMount.dataset.initialized !== 'true') {
          this.initSampleSelector(existingMount, { cartMode: 'rebuy', progressEl: progressMount });
        } else {
          existingMount._sampleRefreshUI?.();
        }
        return true;
      }

      const sourceSelector = S.getSourceSelector();
      if (!sourceSelector) return false;

      S._mountInProgress = true;

      try {
        if (!options.fast) await S.syncRebuyBeforeMount();

        let selectorMount = rebuyCart.querySelector(
          `.sample-selector[data-sample-mount="${this.REBUY_MOUNT}"]`
        );

        if (!selectorMount) {
          selectorMount = sourceSelector.cloneNode(true);
          selectorMount.removeAttribute('id');
          selectorMount.dataset.sampleMount = this.REBUY_MOUNT;
          selectorMount.removeAttribute('data-initialized');
          rebuyCart.appendChild(selectorMount);
        }

        U.Samples.applySampleTranslations(selectorMount);

        U.Samples.setVisible(selectorMount, true);
        this.positionSelectorAboveSubtotal(rebuyCart, selectorMount, options.fast ? 2000 : 1500);

        const sourceProgress = S.getSourceProgress();
        let progressMount = rebuyCart.querySelector(
          `.sample-progress[data-sample-mount="${this.REBUY_MOUNT}"]`
        );

        if (!progressMount && sourceProgress) {
          progressMount = sourceProgress.cloneNode(true);
          progressMount.removeAttribute('id');
          progressMount.dataset.sampleMount = this.REBUY_MOUNT;
          progressMount.removeAttribute('data-initialized');
          rebuyCart.appendChild(progressMount);
        }

        if (progressMount) {
          U.Samples.applySampleTranslations(progressMount);
          this.ensureProgressMount(rebuyCart, progressMount);
        }

        if (selectorMount.dataset.initialized !== 'true') {
          this.initSampleSelector(selectorMount, { cartMode: 'rebuy', progressEl: progressMount });
        } else {
          selectorMount._sampleRefreshUI?.();
        }

        if (progressMount) {
          U.Samples.setVisible(
            progressMount,
            U.Samples.isSampleProgressEnabled() && U.Samples.cartHasItems('rebuy')
          );
        }
        return true;
      } finally {
        S._mountInProgress = false;
      }
    },

    runRebuyMount(options = {}) {
      this.mountRebuySmartCart(options)
        .then((mounted) => {
          if (mounted) U.Samples.scheduleProgressRefresh();
          else this.scheduleRebuyMount({ aggressive: true });
        })
        .catch(() => false);
    },

    scheduleRebuyMount(options = {}) {
      const S = U.Samples;
      if (U.DomSelector.$('.sample-selector.is-adding')) return;

      const aggressive = options.aggressive === true;
      clearTimeout(S._rebuyMountTimer);
      S._rebuyMountTimer = setTimeout(() => this.runRebuyMount({ fast: true }), aggressive ? 50 : 200);

      if (!aggressive) return;

      clearTimeout(S._rebuyMountAggressiveTimer);
      S._rebuyMountAggressiveTimer = setTimeout(() => {
        const rebuyCart = U.Rebuy.findCartRoot();
        const existing = rebuyCart?.querySelector(
          `.sample-selector[data-sample-mount="${this.REBUY_MOUNT}"][data-initialized="true"]`
        );
        if (existing && this.isSelectorPlaced(rebuyCart, existing)) return;
        this.runRebuyMount({ fast: false });
      }, 500);
    },

    nodeMatchesRebuyCartRender(node) {
      if (!(node instanceof Element)) return false;
      const selectors =
        '[data-rebuy-component="cart-subtotal"],' +
        '[data-rebuy-component="cart-items"],' +
        '[data-rebuy-component="footer"],' +
        'cart-subtotal,cart-items,discount-code,' +
        '.rebuy-cart__flyout-subtotal,.rebuy-cart__flyout-item,.rebuy-cart__flyout-footer';
      return node.matches?.(selectors) || !!node.querySelector?.(selectors);
    },

    attachRebuyCartObserver(rebuyCart) {
      if (!rebuyCart || rebuyCart.__ellisSampleCartObserver) return;
      rebuyCart.__ellisSampleCartObserver = true;

      let pendingMount = false;
      let pendingRefresh = false;

      const flushCartMutation = U.debounce(() => {
        const shouldMount = pendingMount;
        const shouldRefresh = pendingRefresh;
        pendingMount = false;
        pendingRefresh = false;

        const existing = rebuyCart.querySelector(
          `.sample-selector[data-sample-mount="${this.REBUY_MOUNT}"][data-initialized="true"]`
        );

        if (shouldRefresh && existing) {
          this.ensureSelectorMount(rebuyCart, existing);
          existing._sampleRefreshUI?.();
          U.Samples.refreshSampleProgressSoon();
          if (!shouldMount) return;
        } else if (shouldRefresh) {
          U.Samples.refreshSampleProgressSoon();
        }

        if (!shouldMount && !shouldRefresh) return;

        if (existing) {
          this.ensureSelectorMount(rebuyCart, existing);
          if (existing.dataset.initialized !== 'true') {
            const progress = rebuyCart.querySelector(
              `.sample-progress[data-sample-mount="${this.REBUY_MOUNT}"]`
            );
            this.initSampleSelector(existing, { cartMode: 'rebuy', progressEl: progress });
          } else {
            existing._sampleRefreshUI?.();
          }
          return;
        }

        if (shouldMount) {
          this.scheduleRebuyMount();
        }
      }, 200);

      const cartObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type !== 'childList') return;

          if (mutation.addedNodes.length || mutation.removedNodes.length) {
            pendingRefresh = true;
          }

          mutation.addedNodes.forEach((node) => {
            if (this.nodeMatchesRebuyCartRender(node)) pendingMount = true;
          });
        });

        if (pendingMount || pendingRefresh) flushCartMutation();
      });

      cartObserver.observe(rebuyCart, { childList: true, subtree: true });
    },

    ensureRebuySampleObserver() {
      if (window.__ellisRebuySampleObserver) return;
      window.__ellisRebuySampleObserver = true;

      const flushRootMutation = U.debounce(() => {
        const rebuyCart = U.Rebuy.findCartRoot();
        if (!rebuyCart) return;

        this.attachRebuyCartObserver(rebuyCart);
        this.scheduleRebuyMount();
      }, 250);

      const rootObserver = new MutationObserver((mutations) => {
        let shouldCheck = false;

        mutations.forEach((mutation) => {
          if (mutation.type !== 'childList') return;
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            if (
              node.id === 'rebuy-cart' ||
              node.id === 'rebuy-smart-cart' ||
              node.matches?.('.rebuy-cart, .rebuy-smart-cart, [data-rebuy-component="smart-cart"]') ||
              node.querySelector?.('#rebuy-cart, .rebuy-cart, [data-rebuy-component="smart-cart"]')
            ) {
              shouldCheck = true;
            }
            if (this.nodeMatchesRebuyCartRender(node)) shouldCheck = true;
          });
        });

        if (shouldCheck) flushRootMutation();
      });

      rootObserver.observe(document.documentElement, { childList: true, subtree: true });

      const rebuyCart = U.Rebuy.findCartRoot();
      if (rebuyCart) this.attachRebuyCartObserver(rebuyCart);
    },

    bootThemeSamples() {
      const root = U.DomSelector.$(`${U.Samples.THEME_SELECTOR}:not([data-sample-mount])`);
      if (!root || root.dataset.initialized === 'true') return;
      if (root.closest('#free-samples-source')) return;
      this.initSampleSelector(root, { cartMode: 'alpine' });
    },

    bindEvents() {
      const S = U.Samples;

      window.__ellisScheduleRebuyMount = (options) => this.scheduleRebuyMount(options);
      window.__ellisRefreshSamples = () => S.scheduleProgressRefresh();

      document.addEventListener('ellis:samples.mount', () => {
        this.scheduleRebuyMount({ aggressive: true });
      });

      document.addEventListener('rebuy:smartcart.ready', () => {
        this.scheduleRebuyMount({ aggressive: true });
      });
      document.addEventListener('rebuy:smartcart.show', () => {
        this.prepareRebuySampleSelector();
        this.scheduleRebuyMount({ aggressive: true });
      });
      document.addEventListener('rebuy:cart.show', () => {
        this.prepareRebuySampleSelector();
      });
      document.addEventListener('rebuy:cart.ready', () => this.scheduleRebuyMount());

      document.addEventListener('alpine:initialized', () => this.bootThemeSamples());
    },

    init() {
      if (window.__ellisSamplesBooted) return;
      window.__ellisSamplesBooted = true;

      this.wireSampleDropdown();
      U.Samples.scheduleRebuyMount = (options) => this.scheduleRebuyMount(options);
      U.Samples.bindCartWatchers();

      this.ensureRebuySampleObserver();
      this.bindEvents();
      U.Samples.applyAllSampleTranslations();
      this.bootThemeSamples();
      this.scheduleRebuyMount();
    },
  };

  /**
   * Keep Rebuy Smart Cart and theme cart drawer mutually exclusive.
   */
  const CartIntegration = {
    name: 'CartIntegration',
    _bootAttempts: 0,
    _maxBootAttempts: 40,
    _bootTimer: null,

    wireSmartCartAdds() {
      if (window.__ellisSmartCartAddWire) return;
      window.__ellisSmartCartAddWire = true;

      document.addEventListener(
        'click',
        async (event) => {
          const cartRoot = event.target.closest(
            '#rebuy-cart, #rebuy-smart-cart, [data-rebuy-component="smart-cart"], .rebuy-cart.is-visible'
          );
          if (!cartRoot) return;

          if (
            event.target.closest(
              '.sample-selector, .sample-select-list, .sample-select-option, .sample-select-toggle'
            )
          ) {
            return;
          }

          const button = event.target.closest(ADD_BUTTON_SELECTOR);
          if (!button || button.disabled || button.dataset.ellisAdding === 'true') return;

          const block = button.closest('.rebuy-product-block');
          if (!block) return;

          const widgetRoot = U.Rebuy.getWidgetRoot(cartRoot, block);
          const variantId = U.Rebuy.getSelectedVariantId(block, widgetRoot);
          if (!variantId) return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          const label = button.querySelector('span') || button;
          const defaultLabel = label.textContent.trim() || 'Add to cart';

          button.dataset.ellisAdding = 'true';
          button.disabled = true;
          label.textContent = 'Adding...';

          try {
            const widget = U.Rebuy.getWidget(widgetRoot);
            const properties = { _source: 'Rebuy' };
            if (widget?.id) properties._widget_id = String(widget.id);

            await U.Rebuy.addViaShopify(
              { id: variantId, quantity: 1, properties },
              { showSmartCart: false }
            );

            document.dispatchEvent(
              new CustomEvent('rebuy.add', {
                detail: { widget, cart: window.Rebuy?.Cart },
              })
            );
          } catch (error) {
            console.error('[CartIntegration] Smart Cart add failed:', error);
            U.handleError(error, 'Add to cart');
          } finally {
            button.dataset.ellisAdding = 'false';
            button.disabled = false;
            label.textContent = defaultLabel;
          }
        },
        true
      );
    },

    closeThemeCart() {
      U.Rebuy.closeThemeCart();
    },

    hideSmartCart() {
      U.Rebuy.hideSmartCart();
    },

    handleSmartCartUrlParam(options = {}) {
      const retryOnly = options.retryOnly === true;

      if (!retryOnly) {
        const params = new URLSearchParams(window.location.search);
        const shouldOpen = params.get('smart_cart') === '1' || params.get('cart') === 'true';
        if (!shouldOpen) return;

        this._openFromUrlParam = true;

        params.delete('smart_cart');
        params.delete('cart');

        const query = params.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
        history.replaceState(null, '', nextUrl);
      } else if (!this._openFromUrlParam) {
        return;
      }

      this.closeThemeCart();
      U.Rebuy.scheduleShowSmartCartWithRetry();
    },

    patchCartOpen() {
      const cart = U.Alpine.getCartStore();
      if (!cart || cart.__ellisCartOpenPatch) return;

      const originalOpen = cart.open.bind(cart);
      cart.open = function openCart() {
        if (window.Rebuy) {
          cart.isOpen = false;
          U.Rebuy.scheduleShowSmartCartWithRetry();
          return;
        }

        U.Rebuy.hideSmartCart();
        originalOpen();
      };

      cart.__ellisCartOpenPatch = true;
    },

    watchThemeCartState() {
      const cart = U.Alpine.getCartStore();
      if (!cart || cart.__ellisCartWatcher || !window.Alpine?.effect) return;

      cart.__ellisCartWatcher = true;
      const integration = this;

      window.Alpine.effect(() => {
        const isOpen = !!cart.isOpen;
        if (!isOpen) return;

        if (window.Rebuy) {
          if (U.Rebuy.isSmartCartVisible()) {
            cart.isOpen = false;
            return;
          }

          cart.isOpen = false;
          U.Rebuy.scheduleShowSmartCartWithRetry();
          return;
        }

        integration.hideSmartCart();
      });
    },

    watchSmartCartVisibility() {
      if (window.__ellisSmartCartVisibilityWatcher) return;
      window.__ellisSmartCartVisibilityWatcher = true;

      const integration = this;
      const enforceExclusivity = () => {
        if (!window.Rebuy) return;
        if (U.Rebuy.isSmartCartVisible()) {
          integration.closeThemeCart();
        }
      };

      ['rebuy:smartcart.show', 'rebuy:cart.show', 'rebuy:smartcart.ready', 'rebuy:cart.ready'].forEach(
        (eventName) => {
          document.addEventListener(eventName, enforceExclusivity);
        }
      );

      const rebuyRoot = U.Rebuy.findCartRoot();
      if (rebuyRoot) {
        const observer = new MutationObserver(enforceExclusivity);
        observer.observe(rebuyRoot, { attributes: true, attributeFilter: ['class', 'style', 'open'] });
      }
    },

    boot() {
      const cart = U.Alpine.getCartStore();

      if (!cart) {
        if (this._bootAttempts >= this._maxBootAttempts) return;
        this._bootAttempts += 1;
        clearTimeout(this._bootTimer);
        this._bootTimer = setTimeout(() => this.boot(), 100);
        return;
      }

      this.patchCartOpen();
      this.watchThemeCartState();
      this.watchSmartCartVisibility();
      U.Rebuy.bindCartAddPatchEvents();
      U.Rebuy.ensureCartAddPatch();

      if (cart.isOpen && window.Rebuy) {
        cart.isOpen = false;
        U.Rebuy.scheduleShowSmartCartWithRetry();
      }

      if (cart.isOpen && U.Rebuy.isSmartCartVisible()) {
        cart.isOpen = false;
      } else if (cart.isOpen) {
        this.hideSmartCart();
      }

      this.handleSmartCartUrlParam({ retryOnly: true });
    },

    bindEvents() {
      document.addEventListener('rebuy:smartcart.show', () => {
        this._openFromUrlParam = false;
        this.closeThemeCart();
        FreeSamples.scheduleRebuyMount({ aggressive: true });
      });

      document.addEventListener('rebuy:cart.show', () => {
        this.closeThemeCart();
      });

      document.addEventListener('rebuy:smartcart.ready', () => {
        this.handleSmartCartUrlParam({ retryOnly: true });
      });

      document.addEventListener('alpine:initialized', () => this.boot());
      document.addEventListener('rebuy:smartcart.ready', () => this.boot());
      document.addEventListener('rebuy:cart.ready', () => this.boot());
    },

    init() {
      if (window.__ellisCartIntegrationInit) return;
      window.__ellisCartIntegrationInit = true;

      this.bindEvents();
      this.handleSmartCartUrlParam();
      this.wireSmartCartAdds();
      U.Rebuy.bindCartAddPatchEvents();
      U.Rebuy.ensureCartAddPatch();
      U.whenReady(() => this.boot());
      this.boot();
    },
  };

  const modules = [RebuySetsWidget, FreeSamples, CartIntegration];

  function boot() {
    modules.forEach((module) => {
      if (typeof module.init === 'function') {
        try {
          module.init();
        } catch (error) {
          console.error(`[custom.js] ${module.name || 'module'} init failed`, error);
        }
      }
    });
  }

  U.whenReady(boot);

  window.EllisCustom = {
    modules: {
      RebuySetsWidget,
      FreeSamples,
      CartIntegration,
    },
    Utility: U,
    register(module) {
      if (!module || typeof module.init !== 'function') return;
      modules.push(module);
      if (module.name) {
        this.modules[module.name] = module;
      }
      module.init();
    },
  };
})();


function enableCookieConsentPointerEvents() {
  const consent = document.querySelector('csm-cookie-consent');

  if (!consent) return false;

  consent.style.setProperty('pointer-events', 'auto', 'important');

  const shadow = consent.shadowRoot;

  if (shadow && !shadow.querySelector('#custom-csm-style')) {
    const style = document.createElement('style');

    style.id = 'custom-csm-style';

    style.textContent = `
      #csm-wrapper {
        pointer-events: auto !important;
      }
    `;

    shadow.appendChild(style);
  }

  return true;
}

// Try immediately
if (!enableCookieConsentPointerEvents()) {
  // Wait for dynamically loaded element
  const observer = new MutationObserver(() => {
    if (enableCookieConsentPointerEvents()) {
      observer.disconnect();
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
}